/**
 * DNA Report Avatar Reducer
 * NgRx reducer for DNA report avatar state management.
 */

import { createReducer, on } from '@ngrx/store';
import * as DnaReportAvatarActions from './dna-report-avatar.actions';
import { initialDnaReportAvatarState } from './dna-report-avatar.state';

export const dnaReportAvatarReducer = createReducer(
  initialDnaReportAvatarState,

  // Fetch bulk avatar images
  on(
    DnaReportAvatarActions.fetchBulkAvatarImages,
    (state, { profileId, imageTypes }) => ({
      ...state,
      isBulkFetching: true,
      error: null,
      profileId,
      // Set loading state for each requested image type
      images: imageTypes.reduce(
        (acc, imageType) => ({
          ...acc,
          [imageType]: {
            ...state.images[imageType],
            imageUrl: state.images[imageType]?.imageUrl ?? '',
            isDefault: state.images[imageType]?.isDefault ?? true,
            loading: true,
            error: null
          }
        }),
        { ...state.images }
      )
    })
  ),

  on(
    DnaReportAvatarActions.fetchBulkAvatarImagesSuccess,
    (state, { profileId, images }) => ({
      ...state,
      isBulkFetching: false,
      profileId,
      images: images.reduce(
        (acc, img) => ({
          ...acc,
          [img.imageType]: {
            imageUrl: img.imageUrl,
            isDefault: img.isDefault,
            width: img.width,
            height: img.height,
            panel: img.panel ?? null,
            loading: false,
            error: null
          }
        }),
        { ...state.images }
      )
    })
  ),

  on(
    DnaReportAvatarActions.fetchBulkAvatarImagesFailure,
    (state, { error }) => ({
      ...state,
      isBulkFetching: false,
      error,
      // Set error state for all loading images
      images: Object.entries(state.images).reduce(
        (acc, [key, value]) => ({
          ...acc,
          [key]: value.loading ? { ...value, loading: false, error } : value
        }),
        {}
      )
    })
  ),

  // Fetch single avatar image
  on(DnaReportAvatarActions.fetchSingleAvatarImage, (state, { imageType }) => ({
    ...state,
    images: {
      ...state.images,
      [imageType]: {
        ...state.images[imageType],
        imageUrl: state.images[imageType]?.imageUrl ?? '',
        isDefault: state.images[imageType]?.isDefault ?? true,
        loading: true,
        error: null
      }
    }
  })),

  on(
    DnaReportAvatarActions.fetchSingleAvatarImageSuccess,
    (state, { imageType, imageUrl, isDefault, width, height, panel }) => ({
      ...state,
      images: {
        ...state.images,
        [imageType]: {
          imageUrl,
          isDefault,
          width,
          height,
          panel: panel ?? null,
          loading: false,
          error: null
        }
      }
    })
  ),

  on(
    DnaReportAvatarActions.fetchSingleAvatarImageFailure,
    (state, { imageType, error }) => ({
      ...state,
      images: {
        ...state.images,
        [imageType]: {
          ...state.images[imageType],
          imageUrl: state.images[imageType]?.imageUrl ?? '',
          isDefault: state.images[imageType]?.isDefault ?? true,
          loading: false,
          error
        }
      }
    })
  ),

  // Set processing state
  on(DnaReportAvatarActions.setProcessing, (state, { isProcessing }) => ({
    ...state,
    isProcessing
  })),

  // Set image loading state
  on(
    DnaReportAvatarActions.setImageLoading,
    (state, { imageType, loading }) => ({
      ...state,
      images: {
        ...state.images,
        [imageType]: {
          ...state.images[imageType],
          imageUrl: state.images[imageType]?.imageUrl ?? '',
          isDefault: state.images[imageType]?.isDefault ?? true,
          loading,
          error: state.images[imageType]?.error ?? null
        }
      }
    })
  ),

  // Clear state
  on(
    DnaReportAvatarActions.clearDnaReportAvatarState,
    () => initialDnaReportAvatarState
  )
);
